/*

  Wordcheck.ts

  Wrapper for word checking and caching

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { TwoLetterTuple } from "./types";
import { GlobalState } from "./globalstate";
import { requestMoves } from "./request";

interface WordCheckResult {
  word: string;
  ok: boolean;
  valid: [string, boolean][];
}

class WordChecker {

  // Global cache for word check results. The cache is first indexed
  // by locale, and then by word.

  private wordCheckCache: Record<string, Record<string, boolean>> = {};

  constructor() {}

  ingestTwoLetterWords(locale: string, twoLetterWords: TwoLetterTuple[]) {
    // Initialize the cache with a list of known two-letter words
    // The two-letter word list contains a list of entries where each
    // entry is an initial letter followed by a list of two-letter words
    // starting with that letter.
    const cache = this.wordCheckCache[locale] ?? (this.wordCheckCache[locale] = {});
    for (const [/* firstLetter */, wordList] of twoLetterWords) {
      for (const word of wordList) {
        cache[word] = true;
      }
    }
  }

  async checkWords(state: GlobalState, locale: string, words: string[]): Promise<boolean> {
    // Return true if all words are valid in the given locale,
    // or false otherwise. Lookups are cached for efficiency.
    let cache = this.wordCheckCache[locale];
    if (cache) {
      let allValid = true;
      for (const word of words) {
        if (cache[word] === undefined) {
          if (word.length === 2)
            // Special case for two-letter words: if they are not
            // in the cache, they are not valid - no roundtrip needed
            return false;
          // Word not found in cache; we need a server roundtrip
          allValid = false;
          break;
        } else if (cache[word] === false) {
          // Word is known to be invalid
          return false;
        }
      }
      if (allValid) {
        // All words are known to be valid
        return true;
      }
    }

    // We need a server roundtrip
    try {
      const response = await requestMoves<WordCheckResult>(state, {
        url: "/wordcheck",
        body: {
          locale,
          word: words[0],
          words,
        }
      });
      if (response?.word === words[0] && response?.valid) {
        // Looks like a valid response
        if (!cache) {
          // We didn't already have a cache for this locale
          cache = this.wordCheckCache[locale] = {};
        }
        // Harvest the returned data into the cache
        for (const [word, result] of response.valid) {
          cache[word] = result;
        }
      }
      return !!response.ok;
    }
    catch (e) {
    }
    return false;
  }
}

// Global word checker cache
export const wordChecker = new WordChecker();
